import { Box } from "@mui/material";
import { Cell, Pie, PieChart, Tooltip as RechartsTooltip } from "recharts";
import { Typography } from "@platform/design-system";

import { TIER_COLORS, TIER_ORDER } from "./constants";
import { fmtCurrency, fmtPct } from "./formatters";

import type { CarrierTier } from "../types";

const TOOLTIP_Z_INDEX = 2;
const CENTER_LABEL_Z_INDEX = 1;
const TOOLTIP_MOUSE_OFFSET = 12;

type DonutDatum = {
  tier: CarrierTier;
  value: number;
};

type DonutTooltipProps = {
  active?: boolean;
  payload?: { payload?: DonutDatum }[];
  total: number;
  valueLabel: string;
  valueFormatter: (value: number) => string;
};

const DonutTooltip = ({
  active,
  payload,
  total,
  valueLabel,
  valueFormatter,
}: DonutTooltipProps): React.JSX.Element | null => {
  const datum = payload?.[0]?.payload;

  if (!active || !datum) return null;

  const percentOfTotal =
    total > 0 ? Math.round((datum.value / total) * 100) : 0;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 2,
        maxWidth: 260,
        p: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
        <Box
          sx={{
            bgcolor: TIER_COLORS[datum.tier],
            borderRadius: "50%",
            height: 10,
            width: 10,
          }}
        />
        <Typography variant="body-medium">
          <strong>{datum.tier}</strong>
        </Typography>
      </Box>
      <Typography variant="body-medium">
        <strong>{valueFormatter(datum.value)}</strong>
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {valueLabel}: {valueFormatter(datum.value)}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        Share of total: {fmtPct(percentOfTotal)}
      </Typography>
    </Box>
  );
};

function DonutChart({
  tier1,
  tier2,
  nonStrategic,
  centerLabel,
  centerSublabel,
  valueLabel = "Premium",
  valueFormatter = fmtCurrency,
  size = 168,
}: {
  tier1: number;
  tier2: number;
  nonStrategic: number;
  centerLabel: string;
  centerSublabel?: string;
  valueLabel?: string;
  valueFormatter?: (value: number) => string;
  size?: number;
}): React.JSX.Element {
  const data: DonutDatum[] = [
    { tier: "Tier 1", value: tier1 },
    { tier: "Tier 2", value: tier2 },
    { tier: "Non-Strategic", value: nonStrategic },
  ];
  const total = tier1 + tier2 + nonStrategic;
  const r = size / 2;
  return (
    <Box
      sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <PieChart width={size} height={size}>
        <Pie
          data={data}
          innerRadius={Math.round(r * 0.68)}
          outerRadius={Math.round(r * 0.96)}
          startAngle={90}
          endAngle={-270}
          dataKey="value"
          stroke="none"
          paddingAngle={1}
          isAnimationActive={false}
        >
          {TIER_ORDER.map((tier) => (
            <Cell key={tier} fill={TIER_COLORS[tier]} />
          ))}
        </Pie>
        <RechartsTooltip
          allowEscapeViewBox={{ x: true, y: true }}
          content={
            <DonutTooltip
              total={total}
              valueLabel={valueLabel}
              valueFormatter={valueFormatter}
            />
          }
          isAnimationActive={false}
          offset={TOOLTIP_MOUSE_OFFSET}
          wrapperStyle={{ pointerEvents: "none", zIndex: TOOLTIP_Z_INDEX }}
        />
      </PieChart>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          pointerEvents: "none",
          whiteSpace: "nowrap",
          zIndex: CENTER_LABEL_Z_INDEX,
        }}
      >
        <Typography>
          <strong>{centerLabel}</strong>
        </Typography>
        {centerSublabel && (
          <Typography variant="caption">{centerSublabel}</Typography>
        )}
      </Box>
    </Box>
  );
}

export { DonutChart };
